import { useEffect, useMemo, useState } from "react";
import {
    X,
    PlusSquare,
    Database,
    BarChart3,
    Sigma,
    GitBranch,
    CalendarDays
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import SingleSelectApiDropdown from "../../components/SingleSelectApiDropdown";
import { CustomDropdown } from "../../components/CustomDropdown ";


interface AddWidgetPayload {
    widget_type: string;
    dataset_name: string;
    title: string;
    metric_field: string | null;
    aggregation: string;
    dimensions: string[];
    group_by_fields: string[];
    time_field: string | null;
    time_granularity: string | null;
    filters: Record<string, any>;
    custom_options: Record<string, any>;
}

interface AddWidgetModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payload: AddWidgetPayload) => Promise<void> | void;
}

interface DropdownItem {
    id?: string | number | null;
    [key: string]: any;
}

interface DatasetField {
    name: string;
    display_name?: string;
    label?: string;
    title?: string;
    role?: string;
    data_type?: string;
    is_groupable?: boolean;
    [key: string]: any;
}

export default function AddWidgetModal({
    isOpen,
    onClose,
    onSubmit
}: AddWidgetModalProps) {
    const [loading, setLoading] = useState(false);
    const [datasetFields, setDatasetFields] = useState<DatasetField[]>([]);
    const [loadingFields, setLoadingFields] = useState(false);

    const [selectedChartType, setSelectedChartType] = useState<DropdownItem | null>(null);
    const [selectedDataset, setSelectedDataset] = useState<DropdownItem | null>(null);

    const [form, setForm] = useState({
        widget_type: "pie",
        dataset_name: "",
        title: "",
        metric_field: "id",
        aggregation: "count",
        group_by_field: "",
        time_field: "",
        time_granularity: "",
        top_n: 10,
        horizontal: false,
        page_size: 10,
        sortable: true,
        fill_gaps: true,
        smooth: false
    });

    const updateForm = (key: string, value: any) => {
        setForm((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const isTimeSeriesWidget = ["line", "area", "timeline", "line_chart", "area_chart"].includes(form.widget_type);
    const isBarLikeWidget = ["bar", "pie", "donut", "bar_chart", "topn"].includes(form.widget_type);
    const isTableWidget = form.widget_type === "table";

    useEffect(() => {
        if (!isOpen) return;

        setForm({
            widget_type: "pie",
            dataset_name: "",
            title: "",
            metric_field: "id",
            aggregation: "count",
            group_by_field: "",
            time_field: "",
            time_granularity: "",
            top_n: 10,
            horizontal: false,
            page_size: 10,
            sortable: true,
            fill_gaps: true,
            smooth: false
        });

        setDatasetFields([]);
        setSelectedChartType(null);
        setSelectedDataset(null);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !form.dataset_name) {
            setDatasetFields([]);
            return;
        }

        const fetchDatasetFields = async () => {
            setLoadingFields(true);
            try {
                const response = await baseapi.get(`/metadata/datasets/${form.dataset_name}/fields`);
                const fields = response.data?.data || response.data?.fields || [];
                setDatasetFields(fields);

                const metricFields = fields.filter((f: DatasetField) =>
                    (f.role || "").toLowerCase() === "metric" ||
                    (f.role || "").toLowerCase() === "identifier" ||
                    ["integer", "float", "number", "decimal"].includes((f.data_type || "").toLowerCase())
                );

                const groupableFields = fields.filter((f: DatasetField) =>
                    (f.role || "").toLowerCase() === "dimension" ||
                    f.is_groupable ||
                    ["string", "enum", "text"].includes((f.data_type || "").toLowerCase())
                );

                const timestampFields = fields.filter((f: DatasetField) =>
                    (f.role || "").toLowerCase() === "timestamp" ||
                    ["date", "datetime", "timestamp"].includes((f.data_type || "").toLowerCase())
                );

                setForm((prev) => ({
                    ...prev,
                    metric_field: metricFields[0]?.name || "id",
                    group_by_field: groupableFields[0]?.name || "",
                    time_field: timestampFields[0]?.name || ""
                }));
            } catch (error: any) {
                showError(error.message || "Failed to load dataset fields");
                setDatasetFields([]);
            } finally {
                setLoadingFields(false);
            }
        };

        fetchDatasetFields();
    }, [form.dataset_name, isOpen]);

    const metricFields = useMemo(() => {
        return datasetFields.filter((f: DatasetField) =>
            (f.role || "").toLowerCase() === "metric" ||
            (f.role || "").toLowerCase() === "identifier" ||
            ["integer", "float", "number", "decimal"].includes((f.data_type || "").toLowerCase())
        );
    }, [datasetFields]);

    const groupableFields = useMemo(() => {
        return datasetFields.filter((f: DatasetField) =>
            (f.role || "").toLowerCase() === "dimension" ||
            f.is_groupable ||
            ["string", "enum", "text"].includes((f.data_type || "").toLowerCase())
        );
    }, [datasetFields]);

    const timestampFields = useMemo(() => {
        return datasetFields.filter((f: DatasetField) =>
            (f.role || "").toLowerCase() === "timestamp" ||
            ["date", "datetime", "timestamp"].includes((f.data_type || "").toLowerCase())
        );
    }, [datasetFields]);

    const handleChartTypeChange = (item: DropdownItem | null) => {
        const nextType = item?.type || "pie";
        setSelectedChartType(item);

        setForm((prev) => ({
            ...prev,
            widget_type: nextType,
            group_by_field: prev.group_by_field,
            time_field: prev.time_field,
            time_granularity: isTimeSeriesWidget ? prev.time_granularity : ""
        }));
    };

    const handleDatasetChange = (item: DropdownItem | null) => {
        setSelectedDataset(item);
        setDatasetFields([]);

        setForm((prev) => ({
            ...prev,
            dataset_name: item?.name || "",
            metric_field: "id",
            group_by_field: "",
            time_field: "",
            time_granularity: ""
        }));
    };

    const buildCustomOptions = () => {
        switch (form.widget_type) {
            case "bar":
            case "bar_chart":
                return {
                    top_n: Number(form.top_n),
                    horizontal: form.horizontal
                };
            case "pie":
                return {
                    top_n: Number(form.top_n)
                };
            case "donut":
                return {
                    top_n: Number(form.top_n),
                    donut: true
                };
            case "table":
                return {
                    page_size: Number(form.page_size),
                    sortable: form.sortable
                };
            case "line":
            case "area":
            case "line_chart":
            case "area_chart":
                return {
                    fill_gaps: form.fill_gaps,
                    smooth: form.smooth
                };
            default:
                return {};
        }
    };

    const handleSubmit = async () => {
        if (!form.title.trim()) {
            showError("Please enter a widget title");
            return;
        }

        if (!form.dataset_name) {
            showError("Please select a dataset");
            return;
        }

        if (!form.metric_field) {
            showError("Please select a metric field");
            return;
        }

        if (!isTableWidget && !form.group_by_field && !isTimeSeriesWidget) {
            showError("Please select a group by field");
            return;
        }

        if (isTimeSeriesWidget && !form.time_field) {
            showError("Please select a time field");
            return;
        }

        setLoading(true);
        try {
            await onSubmit({
                widget_type: form.widget_type,
                dataset_name: form.dataset_name,
                title: form.title.trim(),
                metric_field: form.metric_field || null,
                aggregation: form.aggregation,
                dimensions: form.group_by_field ? [form.group_by_field] : [],
                group_by_fields: form.group_by_field ? [form.group_by_field] : [],
                time_field: form.time_field || null,
                time_granularity: form.time_granularity || null,
                filters: {},
                custom_options: buildCustomOptions()
            });
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-orange-500/10 rounded">
                            <PlusSquare className="w-4 h-4 text-orange-400" />
                        </div>
                        <h3 className="text-base font-bold text-white">Add Widget</h3>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                        type="button"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="flex flex-col min-h-0">
                    <div className="p-5 space-y-4 overflow-y-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-300">
                                    Widget Title <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => updateForm("title", e.target.value)}
                                    placeholder="Widget Title"
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none"
                                />
                            </div>

                            <SingleSelectApiDropdown
                                label="Widget Type"
                                required
                                value={selectedChartType}
                                onChange={handleChartTypeChange}
                                placeholder="Select widget type"
                                apiEndpoint="/dashboard/widgets/types"
                                icon={BarChart3}
                                searchPlaceholder="Search widget types"
                                searchInputPlaceholder="Search widget type..."
                                displayProperty="name"
                                searchFields={["name", "type"]}
                                idProperty="type"
                                responseDataKey="widget_types"
                                showSecondaryDisplayProperty={false}
                                defaultValue={false}
                            />

                            <SingleSelectApiDropdown
                                label="Dataset"
                                required
                                value={selectedDataset}
                                onChange={handleDatasetChange}
                                placeholder="Select dataset"
                                apiEndpoint="/metadata/datasets"
                                icon={Database}
                                searchPlaceholder="Search datasets"
                                searchInputPlaceholder="Search dataset..."
                                displayProperty="title"
                                secondaryDisplayProperty="description"
                                searchFields={["name", "label", "title", "description"]}
                                idProperty="name"
                                additionalQueries={isTimeSeriesWidget ? { type: "timeseries" } : {}}
                                getDisplayText={(item) => item.title || item.label || item.name}
                            />

                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-300">Aggregation</label>
                                <CustomDropdown
                                    data={[
                                        { label: "Count", value: "count" },
                                        { label: "Sum", value: "sum" },
                                        { label: "Average", value: "avg" },
                                        { label: "Min", value: "min" },
                                        { label: "Max", value: "max" }
                                    ]}
                                    selectedValue={form.aggregation}
                                    onSelect={(value: any) => updateForm("aggregation", value)}
                                    placeholder="Select aggregation"
                                    width="w-full"
                                    buttonHeight={11}
                                />
                            </div>
                        </div>

                        {form.dataset_name && !loadingFields && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                                        <Sigma className="w-3 h-3 text-orange-400" />
                                        Metric Field
                                    </label>
                                    <CustomDropdown
                                        data={[
                                            { label: "id", value: "id" },
                                            ...metricFields.map((field) => ({
                                                label: field.display_name || field.label || field.title || field.name,
                                                value: field.name
                                            }))
                                        ]}
                                        selectedValue={form.metric_field}
                                        onSelect={(value: any) => updateForm("metric_field", value)}
                                        placeholder="Select metric field"
                                        width="w-full"
                                        buttonHeight={11}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                                        <GitBranch className="w-3 h-3 text-orange-400" />
                                        Group By Field
                                    </label>
                                    <CustomDropdown
                                        data={groupableFields.map((field) => ({
                                            label: field.display_name || field.label || field.title || field.name,
                                            value: field.name
                                        }))}
                                        selectedValue={form.group_by_field}
                                        onSelect={(value: any) => updateForm("group_by_field", value)}
                                        placeholder="Select a field"
                                        width="w-full"
                                        buttonHeight={11}
                                    />
                                </div>
                            </div>
                        )}

                        {form.dataset_name && isTimeSeriesWidget && !loadingFields && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                                        <CalendarDays className="w-3 h-3 text-orange-400" />
                                        Time Field
                                    </label>
                                    <CustomDropdown
                                        data={timestampFields.map((field) => ({
                                            label: field.display_name || field.label || field.title || field.name,
                                            value: field.name
                                        }))}
                                        selectedValue={form.time_field}
                                        onSelect={(value: any) => updateForm("time_field", value)}
                                        placeholder="Select time field"
                                        width="w-full"
                                        buttonHeight={11}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-300">Time Granularity</label>
                                    <CustomDropdown
                                        data={[
                                            { label: "Hour", value: "hour" },
                                            { label: "Day", value: "day" },
                                            { label: "Week", value: "week" },
                                            { label: "Month", value: "month" },
                                            { label: "Year", value: "year" }
                                        ]}
                                        selectedValue={form.time_granularity}
                                        onSelect={(value: any) => updateForm("time_granularity", value)}
                                        placeholder="Select granularity"
                                        width="w-full"
                                        buttonHeight={11}
                                    />
                                </div>
                            </div>
                        )}

                        {form.dataset_name && isBarLikeWidget && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-300">Top N</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={form.top_n}
                                        onChange={(e) => updateForm("top_n", Number(e.target.value))}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none"
                                    />
                                </div>

                                {(form.widget_type === "bar" || form.widget_type === "bar_chart") && (
                                    <div className="flex items-center gap-2 pt-7">
                                        <input
                                            id="horizontal"
                                            type="checkbox"
                                            checked={form.horizontal}
                                            onChange={(e) => updateForm("horizontal", e.target.checked)}
                                            className="rounded border-gray-600 bg-gray-800 text-orange-500"
                                        />
                                        <label htmlFor="horizontal" className="text-sm text-gray-300">
                                            Horizontal chart
                                        </label>
                                    </div>
                                )}
                            </div>
                        )}

                        {form.dataset_name && isTableWidget && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-300">Page Size</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={form.page_size}
                                        onChange={(e) => updateForm("page_size", Number(e.target.value))}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none"
                                    />
                                </div>

                                <div className="flex items-center gap-2 pt-7">
                                    <input
                                        id="sortable"
                                        type="checkbox"
                                        checked={form.sortable}
                                        onChange={(e) => updateForm("sortable", e.target.checked)}
                                        className="rounded border-gray-600 bg-gray-800 text-orange-500"
                                    />
                                    <label htmlFor="sortable" className="text-sm text-gray-300">
                                        Sortable table
                                    </label>
                                </div>
                            </div>
                        )}

                        {form.dataset_name &&
                            ["line", "area", "line_chart", "area_chart"].includes(form.widget_type) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex items-center gap-2 pt-2">
                                        <input
                                            id="fill_gaps"
                                            type="checkbox"
                                            checked={form.fill_gaps}
                                            onChange={(e) => updateForm("fill_gaps", e.target.checked)}
                                            className="rounded border-gray-600 bg-gray-800 text-orange-500"
                                        />
                                        <label htmlFor="fill_gaps" className="text-sm text-gray-300">
                                            Fill gaps
                                        </label>
                                    </div>

                                    <div className="flex items-center gap-2 pt-2">
                                        <input
                                            id="smooth"
                                            type="checkbox"
                                            checked={form.smooth}
                                            onChange={(e) => updateForm("smooth", e.target.checked)}
                                            className="rounded border-gray-600 bg-gray-800 text-orange-500"
                                        />
                                        <label htmlFor="smooth" className="text-sm text-gray-300">
                                            Smooth line
                                        </label>
                                    </div>
                                </div>
                            )}

                        {loadingFields && (
                            <div className="flex items-center justify-center py-6">
                                <div className="w-5 h-5 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                            </div>
                        )}
                    </div>

                    <div className="px-5 py-4 border-t border-gray-800 bg-gray-800/50 flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-transparent border border-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading || loadingFields}
                            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 border border-orange-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loading && (
                                <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                            )}
                            Add Widget
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}